import { useId, type ReactNode } from 'react';
import { formatCurrency } from '~/utils/paymentUtils';
import { formatChecklistDate } from './checklistWords';

export interface KeptPayment {
  dueOn: string;
  amountCents: number;
  status: string;
}

export interface NewPayment {
  dueOn: string;
  amountCents: number;
}

const KEPT_STATUS_LABELS: Record<string, string> = {
  PAID: 'Paid',
  BEING_PAID: 'Being paid',
  DUE: 'Due',
};

export function keptStatusLabel(status: string): string {
  return KEPT_STATUS_LABELS[status] ?? 'Due';
}

interface ChangeTuitionPaymentsProps {
  /** The signed agreement's payments that stay; the section is left out when there are none. */
  kept?: KeptPayment[];
  /** The payments that are offered, or that replace the remaining ones. */
  payments: NewPayment[];
  /** Heading of the kept section. */
  keptTitle?: string;
  /** Heading of the payments section; "New payments" beside kept ones, "Payments" on a first offer. */
  paymentsTitle?: string;
  totalCents?: number | null;
  /** The office's own row content in place of the amount, such as an input. */
  renderPayment?: (payment: NewPayment, index: number) => ReactNode;
}

// The payments of a tuition agreement being changed: those that stay as they are,
// then the new ones. Dates and money read the way the rest of the family page does.
export function ChangeTuitionPayments({ kept = [], payments, paymentsTitle = 'New payments', keptTitle = 'Staying as they are', totalCents, renderPayment }: ChangeTuitionPaymentsProps) {
  const id = useId();
  return (
    <div className="space-y-4">
      {kept.length > 0 && (
        <section className="space-y-2" aria-labelledby={`${id}-kept`}>
          <h4 id={`${id}-kept`} className="text-sm font-semibold">{keptTitle}</h4>
          <ul className="divide-y rounded-md border">
            {kept.map((payment, index) => (
              <li key={index} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2 text-sm">
                <span>{formatChecklistDate(payment.dueOn)}</span>
                <span>{formatCurrency(payment.amountCents)}</span>
                <span className="text-muted-foreground">{keptStatusLabel(payment.status)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section className="space-y-2" aria-labelledby={`${id}-new`}>
        <div className="flex items-baseline justify-between">
          <h4 id={`${id}-new`} className="text-sm font-semibold">{paymentsTitle}</h4>
          {totalCents != null && <span className="text-sm text-muted-foreground">Total {formatCurrency(totalCents)}</span>}
        </div>
        <ul className="divide-y rounded-md border">
          {payments.map((payment, index) => (
            <li key={index} className="flex flex-wrap items-center gap-3 px-3 py-2">
              <span className="w-28 text-sm">{formatChecklistDate(payment.dueOn)}</span>
              {renderPayment ? renderPayment(payment, index) : <span className="text-sm">{formatCurrency(payment.amountCents)}</span>}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export default ChangeTuitionPayments;
